
import React from "react";
// Stripe/email registration logic restored from previous working version
const API_BASE = "/api";
const NMLS_APP = "NMLS Exam Questions";
const TEST_MODE = false;

const STRIPE_LINK_MONTHLY_TEST = "https://buy.stripe.com/test_fZucN63QQ23baFs6L9fnO00";
const STRIPE_LINK_QUARTERLY_TEST = "https://buy.stripe.com/test_14AcN63QQ9vD5l82uTfnO01";
const STRIPE_LINK_MONTHLY_LIVE = "https://buy.stripe.com/fZucN63QQ23baFs6L9fnO00";
const STRIPE_LINK_QUARTERLY_LIVE = "https://buy.stripe.com/14AcN63QQ9vD5l82uTfnO01";

const STRIPE_LINK_MONTHLY = TEST_MODE ? STRIPE_LINK_MONTHLY_TEST : STRIPE_LINK_MONTHLY_LIVE;
const STRIPE_LINK_QUARTERLY = TEST_MODE ? STRIPE_LINK_QUARTERLY_TEST : STRIPE_LINK_QUARTERLY_LIVE;

export default function RegistrationPage({ plan, onBack }) {
  const [isDarkMode, setIsDarkMode] = React.useState(localStorage.getItem("theme") === "dark");
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [selectedPlan, setSelectedPlan] = React.useState(() => {
    if (plan) return plan;
    const stored = localStorage.getItem("selectedPlan");
    return stored === "quarterly" ? "quarterly" : "monthly";
  });

  React.useEffect(() => {
    if (plan) setSelectedPlan(plan);
  }, [plan]);

  React.useEffect(() => {
    const updateTheme = () => setIsDarkMode(localStorage.getItem("theme") === "dark");
    window.addEventListener("storage", updateTheme);
    return () => window.removeEventListener("storage", updateTheme);
  }, []);

  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRegisterClick = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError("Please fill in all required fields.");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const registerRes = await fetch(`${API_BASE}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userPhone: "",
          userPassword: formData.password,
          userRole: "User",
          userEmail: formData.email,
          appName: NMLS_APP,
        }),
      });

      const text = await registerRes.text();
      if (!registerRes.ok) {
        const message = text?.trim() || "Registration failed.";
        const friendly = /exist|already/i.test(message)
          ? "Email already exists. Please log in instead."
          : message;
        setError(friendly);
        setLoading(false);
        return;
      }

      const redirectUrl = selectedPlan === "monthly" ? STRIPE_LINK_MONTHLY : STRIPE_LINK_QUARTERLY;
      const normalizedEmail = formData.email.trim().toLowerCase();
      const checkoutUrl = new URL(redirectUrl);
      checkoutUrl.searchParams.set("prefilled_email", normalizedEmail);
      checkoutUrl.searchParams.set("client_reference_id", normalizedEmail);

      localStorage.setItem("pendingRegistrationEmail", normalizedEmail);
      localStorage.setItem("pendingRegistrationApp", NMLS_APP);
      localStorage.setItem("selectedPlan", selectedPlan);

      window.location.href = checkoutUrl.toString();
    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-slate-900" : "bg-gradient-to-br from-blue-50 to-indigo-50"}`}>
      <nav className={isDarkMode ? "bg-slate-900 border-b border-slate-700" : "bg-white border-b border-slate-200 shadow-sm"}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="PassMLOExam logo" className="h-10 w-10" />
              <span className={`font-bold text-lg ${isDarkMode ? "text-white" : "text-slate-900"}`}>PassMLOExam</span>
            </a>
            <div className="hidden md:flex items-center gap-6 text-sm font-medium">
              <a href="/" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Home</a>
              <a href="/faq.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>FAQ</a>
              <a href="/resources/index.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Study Guides</a>
              <a href="/mortgage-calculator.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Calculator</a>
              <a href="/contact.html" className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}>Contact</a>
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={isDarkMode ? "text-slate-300 hover:text-blue-400" : "text-slate-700 hover:text-blue-600"}
                title="Toggle dark/light mode"
              >
                {isDarkMode ? "🌙" : "☀️"}
              </button>
            </div>
          </div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center py-10 px-2">
      <div className={`w-full max-w-md border-2 rounded-2xl shadow-2xl p-8 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-blue-200"}`}>
        <h2 className={`text-3xl font-bold mb-2 text-center ${isDarkMode ? "text-white" : "text-slate-900"}`}>Create PassMLOExam Account</h2>
        <div className={`${isDarkMode ? "text-slate-300" : "text-slate-600"} text-center mb-6`}>Start your exam preparation today</div>

        <div className="flex justify-center mb-6">
          <button className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 border ${isDarkMode ? "border-blue-900 bg-blue-950 text-blue-300" : "border-blue-200 bg-blue-50 text-blue-700"}`} disabled>
            <span role="img" aria-label="lock">🔒</span> Secure checkout and verified access
          </button>
        </div>
        <form className="space-y-5" onSubmit={handleRegisterClick}>
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Choose Plan</label>
            <div className="flex flex-col gap-3">
              <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${selectedPlan === "monthly" ? (isDarkMode ? "border-blue-500 bg-slate-700" : "border-blue-500 bg-blue-50") : (isDarkMode ? "border-slate-600" : "border-slate-200")}`}>
                <div>
                  <div className={`font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>1 Month Access</div>
                  <div className="text-2xl font-bold text-orange-500">$50</div>
                </div>
                <input
                  type="radio"
                  name="plan"
                  value="monthly"
                  checked={selectedPlan === "monthly"}
                  onChange={() => setSelectedPlan("monthly")}
                  className="form-radio h-5 w-5 text-blue-600"
                />
              </label>
              <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${selectedPlan === "quarterly" ? (isDarkMode ? "border-blue-500 bg-slate-700" : "border-blue-500 bg-blue-50") : (isDarkMode ? "border-slate-600" : "border-slate-200")}`}>
                <div>
                  <div className={`font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>3 Month Access</div>
                  <div className="text-2xl font-bold text-orange-500">$100</div>
                </div>
                <input
                  type="radio"
                  name="plan"
                  value="quarterly"
                  checked={selectedPlan === "quarterly"}
                  onChange={() => setSelectedPlan("quarterly")}
                  className="form-radio h-5 w-5 text-blue-600"
                />
              </label>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>First Name *</label>
              <input
                type="text"
                name="firstName"
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-slate-600 bg-slate-700 text-white placeholder-slate-400" : "border-slate-200 bg-white text-slate-900"}`}
                value={formData.firstName}
                onChange={handleInputChange}
                required
                placeholder="John"
              />
            </div>
            <div className="flex-1">
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Last Name *</label>
              <input
                type="text"
                name="lastName"
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-slate-600 bg-slate-700 text-white placeholder-slate-400" : "border-slate-200 bg-white text-slate-900"}`}
                value={formData.lastName}
                onChange={handleInputChange}
                required
                placeholder="Doe"
              />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Email *</label>
            <input
              type="email"
              name="email"
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-slate-600 bg-slate-700 text-white placeholder-slate-400" : "border-slate-200 bg-white text-slate-900"}`}
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Password *</label>
            <input
              type="password"
              name="password"
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-slate-600 bg-slate-700 text-white placeholder-slate-400" : "border-slate-200 bg-white text-slate-900"}`}
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder="Min. 6 characters"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-slate-600 bg-slate-700 text-white placeholder-slate-400" : "border-slate-200 bg-white text-slate-900"}`}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              placeholder="Repeat password"
            />
          </div>
          {error && <div className={`text-sm text-center ${isDarkMode ? "text-red-300" : "text-red-600"}`}>{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition text-lg mt-2"
          >
            {loading ? "Redirecting to Stripe..." : "Continue to Payment"}
          </button>
        </form>
        <button
          className={`mt-6 w-full hover:underline text-sm ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
          onClick={onBack}
        >
          Back
        </button>
      </div>
      </div>
      <footer className={`border-t ${isDarkMode ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`text-xs text-center space-y-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            <div>© 2026 PassMLOExam. All rights reserved.</div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <a href="/faq.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>FAQ</a>
              <a href="/contact.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Contact Us</a>
              <a href="/privacy.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Privacy</a>
              <a href="/copyright.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Copyright</a>
              <a href="/resources/index.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Study Guides</a>
              <a href="/mortgage-calculator.html" className={isDarkMode ? "hover:text-slate-300" : "hover:text-slate-700"}>Calculator</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
